import { Check, Copy, X } from '@phosphor-icons/react';
import { useTranslation } from '@client-app/i18n';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './PluginSetupModal.module.scss';
import {
    getPluginSetupHelperTranslationKey,
    type PluginSetupCopyState,
} from './pluginSetupCopyFeedback';

type AiAssistant = 'chatgpt' | 'claude' | 'workbuddy';

declare const __WEBSITE_ASSET_BASE_URL__: string;

const getPluginSetupAssetUrl = (assetPath: string) => {
    const baseUrl = __WEBSITE_ASSET_BASE_URL__ || '/';
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    return `${normalizedBaseUrl}${assetPath.replace(/^\/+/, '')}`;
};

const AI_ASSISTANT_PROMPTS: Record<AiAssistant, string> = {
    chatgpt: "Use Codex's custom plugin marketplace to add `BizSidekick-AI/bizsidekick` and install BizSidekick if needed. Do not open or read the repository in a browser. Reuse my existing sign-in. If authorization completes without account setup, verify the connection and automatically create and open one new BizSidekick task that shows my stores and recent products. If login, registration, or BizSidekick onboarding is required, keep this installation task open, ask me to finish in the browser and reply `Continue`, then verify authorization and create the task.",
    claude: "Use Claude Code's custom plugin marketplace to add `BizSidekick-AI/bizsidekick` and install `bizsidekick@bizsidekick` if needed. Do not open or read the repository in a browser. Preserve my existing sign-in and run `/reload-plugins` once after a new install. If authorization completes without account setup, continue in this session and automatically start a read-only BizSidekick task that shows my stores and recent products. If login, registration, or BizSidekick onboarding is required, keep this session open, ask me to finish in the browser and reply `Continue`, then verify authorization and start the task.",
    workbuddy: "Use WorkBuddy's custom plugin marketplace to add `BizSidekick-AI/bizsidekick` and install `bizsidekick@bizsidekick` if needed. Do not open or read the repository in a browser. Preserve my existing sign-in and run `/reload-plugins` once after a new install. If authorization completes without account setup, continue in this conversation and automatically start a read-only BizSidekick task that shows my stores and recent products. If login, registration, or BizSidekick onboarding is required, keep this conversation open, ask me to finish in the browser and reply `Continue`, then verify authorization and start the task.",
};

const AI_ASSISTANTS: Array<{ id: AiAssistant; label: string; icon: string; screenshot?: string }> = [
    {
        id: 'chatgpt',
        label: 'ChatGPT',
        icon: getPluginSetupAssetUrl('/brand/ai-platforms/chatgpt.svg'),
        screenshot: getPluginSetupAssetUrl('/imgs/plugin-setup/chatgpt-input.png'),
    },
    {
        id: 'claude',
        label: 'Claude',
        icon: getPluginSetupAssetUrl('/brand/ai-platforms/claude.svg'),
        screenshot: getPluginSetupAssetUrl('/imgs/plugin-setup/claude-input.png'),
    },
    {
        id: 'workbuddy',
        label: 'WorkBuddy',
        icon: getPluginSetupAssetUrl('/brand/ai-platforms/workbuddy.svg'),
        screenshot: getPluginSetupAssetUrl('/imgs/plugin-setup/workbuddy-input.png'),
    },
];

async function copyPrompt(prompt: string) {
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(prompt);
            return;
        } catch {
            // Clipboard access can be unavailable in embedded previews.
        }
    }

    const textarea = document.createElement('textarea');
    textarea.value = prompt;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const didCopy = document.execCommand('copy');
    textarea.remove();

    if (!didCopy) throw new Error('Clipboard is unavailable');
}

export interface PluginSetupModalProps {
    isOpen: boolean;
    onClose: () => void;
    titleKey?: string;
}

export const PluginSetupModal: React.FC<PluginSetupModalProps> = ({ isOpen, onClose, titleKey = 'website.pluginSetup.title' }) => {
    const { t } = useTranslation();
    const [assistant, setAssistant] = useState<AiAssistant>('chatgpt');
    const [copyState, setCopyState] = useState<PluginSetupCopyState>('idle');
    const resetTimerRef = useRef<number | null>(null);
    const selectedAssistant = AI_ASSISTANTS.find((item) => item.id === assistant)!;

    useEffect(() => () => {
        if (resetTimerRef.current !== null) window.clearTimeout(resetTimerRef.current);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setCopyState('idle');
            setAssistant('chatgpt');
        }
    }, [isOpen]);

    const handleAssistantChange = (nextAssistant: AiAssistant) => {
        setAssistant(nextAssistant);
        setCopyState('idle');
    };

    const handleCopy = async () => {
        if (resetTimerRef.current !== null) window.clearTimeout(resetTimerRef.current);

        try {
            await copyPrompt(AI_ASSISTANT_PROMPTS[assistant]);
            setCopyState('copied');
        } catch {
            setCopyState('error');
        }

        resetTimerRef.current = window.setTimeout(() => setCopyState('idle'), 1500);
    };

    if (!isOpen) return null;

    return createPortal(
        <div className={styles.overlay} onMouseDown={onClose} role="presentation">
            <section
                aria-modal="true"
                aria-labelledby="plugin-setup-title"
                className={styles.modal}
                onMouseDown={(event) => event.stopPropagation()}
                role="dialog"
            >
                <button className={styles.closeButton} type="button" onClick={onClose} aria-label={t('website.pluginSetup.close')}>
                    <X size={20} />
                </button>
                <header className={styles.header}>
                    <h2 id="plugin-setup-title">{t(titleKey)}</h2>
                </header>

                <div className={styles.tabs} role="tablist" aria-label={t('website.pluginSetup.chooseApp')}>
                    {AI_ASSISTANTS.map((item) => (
                        <button
                            aria-selected={assistant === item.id}
                            className={assistant === item.id ? styles.activeTab : styles.tab}
                            key={item.id}
                            onClick={() => handleAssistantChange(item.id)}
                            role="tab"
                            type="button"
                        >
                            <img src={item.icon} alt="" aria-hidden="true" />
                            {item.label}
                        </button>
                    ))}
                </div>

                <div className={styles.content}>
                    <div className={styles.steps}>
                        <div className={styles.step}>
                            <span>1</span>
                            <div>
                                <strong>{t('website.pluginSetup.stepCopyTitle', { app: selectedAssistant.label })}</strong>
                            </div>
                        </div>
                        <div className={styles.step}>
                            <span>2</span>
                            <div>
                                <strong>{t('website.pluginSetup.stepAuthorizeTitle')}</strong>
                            </div>
                        </div>
                    </div>

                    {selectedAssistant.screenshot ? (
                        <img
                            key={selectedAssistant.id}
                            className={styles.productScreenshot}
                            src={selectedAssistant.screenshot}
                            alt={t('website.pluginSetup.screenshotAlt', { app: selectedAssistant.label })}
                        />
                    ) : (
                        <div className={styles.screenshotPlaceholder} aria-label={t('website.pluginSetup.screenshotPlaceholderAlt', { app: selectedAssistant.label })}>
                            <div className={styles.placeholderBrowserBar}><i /><i /><i /></div>
                            <div className={styles.placeholderContent}>
                                <img src={selectedAssistant.icon} alt="" aria-hidden="true" />
                                <strong>{t('website.pluginSetup.screenshotPlaceholderTitle', { app: selectedAssistant.label })}</strong>
                                <span>{t('website.pluginSetup.screenshotPlaceholderDescription')}</span>
                            </div>
                        </div>
                    )}
                </div>

                <footer className={styles.footer}>
                    <button
                        aria-label={copyState === 'copied' ? t('website.pluginSetup.copied') : undefined}
                        className={styles.copyButton}
                        type="button"
                        onClick={() => void handleCopy()}
                    >
                        {copyState === 'copied' ? <Check size={18} weight="bold" /> : <Copy size={18} weight="bold" />}
                        {copyState === 'copied'
                            ? t('website.pluginSetup.copied')
                            : t('website.pluginSetup.copyButton', { app: selectedAssistant.label })}
                    </button>
                    <p aria-live="polite" className={copyState === 'error' ? styles.error : styles.helper}>
                        {t(
                            getPluginSetupHelperTranslationKey(copyState),
                            copyState === 'copied' ? { app: selectedAssistant.label } : undefined,
                        )}
                    </p>
                </footer>
            </section>
        </div>,
        document.body,
    );
};
